import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import API from '../api';
import SearchSelect from '../components/SearchSelect';

const ROLE_ICONS = { admin: 'bi-shield-check', enseignant: 'bi-mortarboard', etudiant: 'bi-person-badge' };

export default function Profile() {
  const { user, setUser } = useAuth();
  const { addToast } = useToast();
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', faculte: '', promotion: '' });
  const [pwForm, setPwForm] = useState({ old_password: '', new_password: '' });
  const [facultes, setFacultes] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const fileRef = useRef(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  useEffect(() => {
    API.get('facultes/').then((r) => setFacultes(r.data.results || r.data)).catch(() => {});
    API.get('promotions/').then((r) => setPromotions(r.data.results || r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || '', last_name: user.last_name || '',
        email: user.email || '', phone: user.phone || '',
        faculte: user.faculte || '', promotion: user.promotion || '',
      });
    }
  }, [user]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handlePwChange = (e) => setPwForm({ ...pwForm, [e.target.name]: e.target.value });

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarPreview(URL.createObjectURL(file));
    const fd = new FormData();
    fd.append('avatar', file);
    try {
      const res = await API.patch('profile/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (setUser) setUser(res.data);
      addToast('Photo de profil mise à jour', 'success');
    } catch {
      addToast('Erreur lors de l\'envoi de la photo', 'error');
      setAvatarPreview(null);
    }
  };

  const handleProfile = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.faculte) payload.faculte = null;
      if (!payload.promotion) payload.promotion = null;
      const res = await API.patch('profile/', payload);
      if (setUser) setUser(res.data);
      addToast('Profil mis à jour avec succès', 'success');
    } catch (err) {
      addToast(Object.values(err.response?.data || {}).flat().join(' ') || 'Erreur lors de la mise à jour', 'error');
    }
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    try {
      await API.post('profile/change-password/', pwForm);
      addToast('Mot de passe modifié avec succès', 'success');
      setPwForm({ old_password: '', new_password: '' });
    } catch (err) {
      addToast(Object.values(err.response?.data || {}).flat().join(' ') || 'Erreur lors du changement de mot de passe', 'error');
    }
  };

  if (!user) return null;

  return (
    <div>
      <div className="page-header">
        <h2><i className="bi bi-person-circle" /> Mon profil</h2>
        <p>Gérez vos informations personnelles et votre mot de passe</p>
      </div>

      {/* ── Avatar ── */}
      <div className="form-glass profile-avatar-section">
        <div className="profile-avatar-wrap">
          <div className="profile-avatar-lg" onClick={() => fileRef.current?.click()}>
            {(avatarPreview || user.avatar) ? (
              <img src={avatarPreview || (user.avatar.startsWith('http') ? user.avatar : `${process.env.REACT_APP_API_URL || 'https://admined.cgiibnn-esursi.cd'}${user.avatar}`)} alt="avatar" />
            ) : (
              <span>{`${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() || 'U'}</span>
            )}
            <div className="profile-avatar-overlay"><i className="bi bi-camera-fill" /></div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
          <div className="profile-avatar-info">
            <h3>{user.first_name} {user.last_name}</h3>
            <p>{user.role_display || user.role} {user.universite_nom ? `— ${user.universite_nom}` : ''}</p>
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => fileRef.current?.click()}>
              <i className="bi bi-camera" /> Changer la photo
            </button>
          </div>
        </div>
      </div>

      {/* ── Infos du compte (lecture seule) ── */}
      <div className="form-glass">
        <h3><i className={`bi ${ROLE_ICONS[user.role] || 'bi-person'}`} /> Informations du compte</h3>
        <div className="profile-info-grid">
          <div className="profile-info-item">
            <span className="profile-info-label">Nom d'utilisateur</span>
            <span className="profile-info-value">{user.username}</span>
          </div>
          <div className="profile-info-item">
            <span className="profile-info-label">Rôle</span>
            <span className="profile-info-value">
              <span className={`badge-status ${user.role === 'admin' ? 'badge-warning' : user.role === 'enseignant' ? 'badge-success' : 'badge-muted'}`}>
                {user.role_display || user.role}
              </span>
            </span>
          </div>
          {user.universite_nom && (
            <div className="profile-info-item">
              <span className="profile-info-label"><i className="bi bi-building" /> Université</span>
              <span className="profile-info-value">{user.universite_nom}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Modifier informations personnelles ── */}
      <div className="form-glass">
        <h3><i className="bi bi-person-lines-fill" /> Informations personnelles</h3>
        <form onSubmit={handleProfile}>
          <div className="form-grid">
            <div className="form-group">
              <label>Prénom</label>
              <input className="form-control" name="first_name" value={form.first_name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Nom</label>
              <input className="form-control" name="last_name" value={form.last_name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input className="form-control" name="email" type="email" value={form.email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Téléphone</label>
              <input className="form-control" name="phone" value={form.phone} onChange={handleChange} placeholder="+243 ..." />
            </div>
            {(user.role === 'enseignant' || user.role === 'etudiant') && (
              <div className="form-group">
                <label><i className="bi bi-bank" /> Faculté</label>
                <SearchSelect
                  name="faculte"
                  value={form.faculte}
                  onChange={handleChange}
                  options={facultes.map((f) => ({ value: f.id, label: f.nom }))}
                  placeholder="-- Sélectionner --"
                />
              </div>
            )}
            {user.role === 'etudiant' && (
              <div className="form-group">
                <label><i className="bi bi-mortarboard" /> Promotion</label>
                <SearchSelect
                  name="promotion"
                  value={form.promotion}
                  onChange={handleChange}
                  options={promotions.map((p) => ({ value: p.id, label: p.nom }))}
                  placeholder="-- Sélectionner --"
                />
              </div>
            )}
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary"><i className="bi bi-check-lg" /> Enregistrer</button>
          </div>
        </form>
      </div>

      {/* ── Changer mot de passe ── */}
      <div className="form-glass">
        <h3><i className="bi bi-shield-lock" /> Changer le mot de passe</h3>
        <form onSubmit={handlePassword}>
          <div className="form-grid">
            <div className="form-group">
              <label>Ancien mot de passe</label>
              <input className="form-control" name="old_password" type="password" value={pwForm.old_password} onChange={handlePwChange} required />
            </div>
            <div className="form-group">
              <label>Nouveau mot de passe</label>
              <input className="form-control" name="new_password" type="password" value={pwForm.new_password} onChange={handlePwChange} required />
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary"><i className="bi bi-key" /> Modifier</button>
          </div>
        </form>
      </div>
    </div>
  );
}
